// src/features/businessdetails/businessdetailsApiSlice.js
import { createEntityAdapter, createSelector } from '@reduxjs/toolkit';
import { apiSlice } from '../../app/api/apiSlice';

// --- Normalized adapter ---
const businessdetailsAdapter = createEntityAdapter({
  // use our own string "id" we set in transformResponse
  selectId: (c) => c.id,
});

// Keep a stable extra field for pagination metadata
const initialState = businessdetailsAdapter.getInitialState({ total: 0 });

export const businessdetailsApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getBusinessdetails: builder.query({
        // args: { page, limit, q, status, sortBy, order, ... }
        query: (params = {}) => ({ url: '/businessdetails', params }),

        transformResponse: (response, meta) => {
          let items = [];
          let total = 0;

          if (response && typeof response === 'object' && 'items' in response && 'total' in response) {
            items = response.items ?? [];
            total = Number(response.total ?? 0);
          } else if (Array.isArray(response)) {
            items = response;
            const headerTotal =
              meta?.response?.headers?.get('X-Total-Count') ??
              meta?.response?.headers?.get('x-total-count');
            total = headerTotal ? Number(headerTotal) : items.length;
          }

          // 1) Force a *single* stable string key, and set BOTH id and _id to it
          const processed = items.map((c) => {
            const raw = c?._id ?? c?.id;
            const sid = raw != null ? String(raw) : undefined;
            return { ...c, id: sid, _id: sid };
          });

          // 2) Manual normalization using *id* as the key (matches adapter.selectId)
          const ids = [];
          const entities = {};
          for (const c of processed) {
            if (c.id) {
              ids.push(c.id);
              entities[c.id] = c;
            }
          }

          // Optional debug (remove after verifying)
          // console.log('normalized (id):', { idsLen: ids.length, total, first: ids[0] });

          return { ids, entities, total };
        },
      providesTags: (result) =>
        result?.ids
          ? [
              ...result.ids.map((id) => ({ type: 'Businessdetail', id })),
              { type: 'Businessdetail', id: 'LIST' }, // ⬅️ use LIST consistently
            ]
          : [{ type: 'Businessdetail', id: 'LIST' }],
    }),

    getBusinessdetailById: builder.query({
      query: (id) => `/businessdetails/${id}`,
      providesTags: (_res, _err, id) => [{ type: 'Businessdetail', id }],
    }),

    addNewBusinessdetail: builder.mutation({
      query: (body) => ({ url: '/businessdetails', method: 'POST', body }),
      invalidatesTags: [{ type: 'Businessdetail', id: 'LIST' }], // ⬅️ matches providesTags
    }),

    updateBusinessdetail: builder.mutation({
      query: ({ id, ...patch }) => ({ url: `/businessdetails/${id}`, method: 'PATCH', body: patch }),
      invalidatesTags: (_res, _err, { id }) => [{ type: 'Businessdetail', id }, { type: 'Businessdetail', id: 'LIST' }],
    }),

    deleteBusinessdetail: builder.mutation({
      query: ({ id }) => ({ url: `/businessdetails/${id}`, method: 'DELETE', body: { id } }),
      invalidatesTags: [{ type: 'Businessdetail', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetBusinessdetailsQuery,
  useGetBusinessdetailByIdQuery,
  useAddNewBusinessdetailMutation,
  useUpdateBusinessdetailMutation,
  useDeleteBusinessdetailMutation,
} = businessdetailsApiSlice;

/* -----------------------
   Arg-aware selectors
   ----------------------- */

// Base result selector factory (must pass the same args you use in the hook)
const selectGetBusinessdetailsResult = businessdetailsApiSlice.endpoints.getBusinessdetails.select;

export const makeSelectBusinessdetailsData = (args) =>
  createSelector(selectGetBusinessdetailsResult(args), (res) => res?.data ?? initialState);

// Export a factory that returns normalized selectors + total for a given args set
export const makeBusinessdetailSelectors = (args) => {
  const selectData = makeSelectBusinessdetailsData(args);
  const base = businessdetailsAdapter.getSelectors((state) => selectData(state));
  return {
    selectAllBusinessdetails: base.selectAll,
    selectBusinessdetailById: base.selectById,
    selectBusinessdetailIds: base.selectIds,
    selectBusinessdetailEntities: base.selectEntities,
    selectTotal: (state) => selectData(state).total ?? 0,
  };
};
